import React, { useEffect, useState } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import { Card, CardContent, Typography, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

export interface StatCardProps {
  title: string;
  value: number;
  trend?: 'up' | 'down';
  trendValue?: number;
  icon?: React.ReactNode;
  moduleColor: string;
  delay?: number;
  suffix?: string;
  prefix?: string;
}

/**
 * StatCard - Dashboard metric card with editorial style
 *
 * Features:
 * - Animated number counter (counts up from 0)
 * - Module color accent bar (4px left border)
 * - Trend indicator with percentage
 * - Staggered reveal animation
 * - Smooth hover elevation
 * - Optimized with React.memo to prevent unnecessary re-renders
 *
 * Usage:
 * <StatCard
 *   title="Emails Sent Today"
 *   value={1234}
 *   trend="up"
 *   trendValue={12}
 *   moduleColor={theme.palette.email.main}
 *   delay={0.1}
 * />
 */
export const StatCard: React.FC<StatCardProps> = React.memo(({
  title,
  value,
  trend,
  trendValue,
  icon,
  moduleColor,
  delay = 0,
  suffix = '',
  prefix = '',
}) => {
  const theme = useTheme();
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const controls = useAnimation();

  const [displayValue, setDisplayValue] = useState(0);

  // Animated counter effect
  useEffect(() => {
    if (isInView) {
      controls.start('visible');

      const duration = 1200;
      const steps = 60;
      const increment = value / steps;
      let currentStep = 0;

      const timer = setInterval(() => {
        currentStep++;
        if (currentStep <= steps) {
          setDisplayValue(Math.round(increment * currentStep));
        } else {
          setDisplayValue(value);
          clearInterval(timer);
        }
      }, duration / steps);

      return () => clearInterval(timer);
    }
  }, [isInView, value, controls]);

  const variants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        delay,
        ease: [0.25, 0.8, 0.25, 1] as const,
      },
    },
  };

  const getTrendColor = () => {
    if (!trend) return theme.palette.text.secondary;
    return trend === 'up' ? theme.palette.success.main : theme.palette.error.main;
  };

  return (
    <motion.div
      ref={ref}
      variants={variants}
      initial="hidden"
      animate={controls}
      style={{ height: '100%' }}
    >
      <Card
        sx={{
          height: '100%',
          position: 'relative',
          overflow: 'visible',
          transition: `all ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}`,
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[4],
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            backgroundColor: moduleColor,
            transition: `width ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}`,
          },
          '&:hover::before': {
            width: 6,
          },
        }}
      >
        <CardContent
          sx={{
            padding: theme.spacing(3),
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          {/* Header with title and icon */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: theme.spacing(2),
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 600,
                letterSpacing: '0.08em',
              }}
            >
              {title}
            </Typography>
            {icon && (
              <Box
                sx={{
                  color: moduleColor,
                  opacity: 0.6,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: theme.shape.borderRadius,
                  backgroundColor: `${moduleColor}15`,
                  transition: `all ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}`,
                  '&:hover': {
                    opacity: 1,
                    transform: 'scale(1.05)',
                  },
                }}
              >
                {icon}
              </Box>
            )}
          </Box>

          {/* Animated value */}
          <Box sx={{ marginBottom: theme.spacing(1.5) }}>
            <Typography
              variant="h2"
              component={motion.div}
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 700,
                color: theme.palette.text.primary,
                lineHeight: 1.2,
              }}
            >
              {prefix}
              {displayValue.toLocaleString()}
              {suffix}
            </Typography>
          </Box>

          {/* Trend indicator */}
          {trend && trendValue !== undefined && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: delay + 0.6, duration: 0.3 }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  padding: theme.spacing(0.5, 1),
                  borderRadius: theme.shape.borderRadius / 2,
                  backgroundColor:
                    trend === 'up'
                      ? `${theme.palette.success.main}15`
                      : `${theme.palette.error.main}15`,
                }}
              >
                {trend === 'up' ? (
                  <TrendingUpIcon sx={{ fontSize: 16, color: getTrendColor() }} />
                ) : (
                  <TrendingDownIcon sx={{ fontSize: 16, color: getTrendColor() }} />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    color: getTrendColor(),
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                  }}
                >
                  {trendValue > 0 ? '+' : ''}
                  {trendValue}%
                </Typography>
              </Box>
            </motion.div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison to prevent re-renders when props haven't changed
  return (
    prevProps.title === nextProps.title &&
    prevProps.value === nextProps.value &&
    prevProps.trend === nextProps.trend &&
    prevProps.trendValue === nextProps.trendValue &&
    prevProps.moduleColor === nextProps.moduleColor &&
    prevProps.delay === nextProps.delay &&
    prevProps.suffix === nextProps.suffix &&
    prevProps.prefix === nextProps.prefix
  );
});
